import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import Logo from "@/components/Logo";
import { QRCodeSVG } from "qrcode.react";

/*
  Unified printable pages. Each variant renders a clean print-friendly A4 layout.
  Print CSS keeps only the .print-sheet visible.
*/

function PrintFrame({ title, children }) {
  useEffect(() => {
    document.title = title || "Print · Anveshan";
    const t = setTimeout(() => window.print(), 500);
    return () => clearTimeout(t);
  }, [title]);
  return (
    <>
      <style>{`
        @media print {
          body { background: #fff !important; }
          .no-print { display: none !important; }
        }
        .print-sheet { max-width: 820px; margin: 0 auto; background: #fff; padding: 40px; box-shadow: 0 0 0 1px #E5D9CC; }
        .print-hr { border: none; border-top: 1px solid #E5D9CC; margin: 16px 0; }
      `}</style>
      <div style={{ background: "#F9F6F0", minHeight: "100vh", padding: 24 }}>
        <div className="no-print" style={{ maxWidth: 820, margin: "0 auto 12px", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={() => window.print()} className="samaaj-btn-primary text-sm">Print now</button>
          <button onClick={() => window.close()} className="samaaj-btn-outline text-sm">Close</button>
        </div>
        <div className="print-sheet">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Logo size={36} />
            <div style={{ fontSize: 11, color: "#5C564D" }}>Generated {new Date().toLocaleString()}</div>
          </div>
          <hr className="print-hr" />
          {children}
          <hr className="print-hr" />
          <div style={{ fontSize: 10, color: "#5C564D", textAlign: "center", marginTop: 12 }}>
            Anveshan — Community Services · anveshansociety.com
          </div>
        </div>
      </div>
    </>
  );
}

function useFetch(url) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(url); setData(data); } catch (e) { setErr(e.response?.data?.detail || "Not found"); }
    })();
  }, [url]);
  return { data, err };
}

export function PrintOrder() {
  const { id } = useParams();
  const { data } = useFetch(`/orders/mine`);
  const o = (data || []).find((x) => x.id === id);
  if (!o) return <PrintFrame title="Order"><p>Loading order…</p></PrintFrame>;
  return (
    <PrintFrame title={`Order · ${o.id}`}>
      <h1 style={{ fontFamily: "serif", fontSize: 28, margin: 0 }}>Order Receipt</h1>
      <div style={{ marginTop: 8, fontSize: 12, color: "#5C564D" }}>Order ID: {o.id}</div>
      <hr className="print-hr" />
      <table style={{ width: "100%", fontSize: 14 }}>
        <tbody>
          <Row k="Item" v={o.listing_title || "Marketplace item"} />
          <Row k="Amount" v={o.amount_total ? `$${(o.amount_total / 100).toFixed(2)}` : o.amount ? `$${o.amount}` : "—"} />
          <Row k="Currency" v={(o.currency || "usd").toUpperCase()} />
          <Row k="Payment" v={o.payment_status || o.status || "—"} />
          <Row k="Placed on" v={o.created_at ? new Date(o.created_at).toLocaleString() : "—"} />
          {o.buyer_name && <Row k="Buyer" v={o.buyer_name} />}
          {o.buyer_email && <Row k="Email" v={o.buyer_email} />}
        </tbody>
      </table>
    </PrintFrame>
  );
}

export function PrintApplication() {
  const { id } = useParams();
  const { data } = useFetch(`/jobs/applications/mine`);
  const a = (data || []).find((x) => x.id === id);
  if (!a) return <PrintFrame title="Application"><p>Loading application…</p></PrintFrame>;
  return (
    <PrintFrame title={`Application · ${a.job_title}`}>
      <h1 style={{ fontFamily: "serif", fontSize: 28, margin: 0 }}>Job Application</h1>
      <div style={{ marginTop: 8, fontSize: 12, color: "#5C564D" }}>Ref: {a.id}</div>
      <hr className="print-hr" />
      <table style={{ width: "100%", fontSize: 14 }}>
        <tbody>
          <Row k="Role" v={a.job_title} />
          <Row k="Applicant" v={a.applicant_name} />
          <Row k="Email" v={a.applicant_email} />
          <Row k="Qualification" v={a.user_qualification} />
          <Row k="Status" v={a.is_qualified ? "Qualified" : "Under review"} />
          <Row k="Applied on" v={new Date(a.created_at).toLocaleString()} />
        </tbody>
      </table>
      {a.cover_letter && (<><div style={{ marginTop: 16, fontWeight: 700 }}>Cover letter</div><p style={{ whiteSpace: "pre-wrap", fontSize: 13 }}>{a.cover_letter}</p></>)}
    </PrintFrame>
  );
}

export function PrintCertificate() {
  const { id } = useParams();
  const { data } = useFetch(`/training/certificates`);
  const c = (data || []).find((x) => x.id === id);
  if (!c) return <PrintFrame title="Certificate"><p>Loading certificate…</p></PrintFrame>;
  return (
    <PrintFrame title={`Certificate · ${c.program_title}`}>
      <div style={{ textAlign: "center", padding: "32px 0" }}>
        <div style={{ fontSize: 12, letterSpacing: 4, color: "#D95A41", fontWeight: 700 }}>ANVESHAN COMMUNITY ACADEMY</div>
        <h1 style={{ fontFamily: "serif", fontSize: 40, margin: "16px 0" }}>Certificate of Completion</h1>
        <div style={{ fontSize: 14, color: "#5C564D" }}>This is to certify that</div>
        <div style={{ fontFamily: "serif", fontSize: 32, margin: "8px 0", color: "#2C4A3B" }}>{c.user_name}</div>
        <div style={{ fontSize: 14, color: "#5C564D" }}>has successfully completed the training program</div>
        <div style={{ fontFamily: "serif", fontSize: 22, margin: "8px 0" }}>{c.program_title}</div>
        <div style={{ fontSize: 12, color: "#5C564D" }}>{c.program_duration} · {c.level}</div>
        <hr className="print-hr" style={{ margin: "24px 20%" }} />
        <div style={{ display: "flex", justifyContent: "space-between", padding: "0 40px", fontSize: 12 }}>
          <div><div style={{ fontWeight: 700 }}>Certificate No.</div><div>{c.certificate_no}</div></div>
          <div><div style={{ fontWeight: 700 }}>Issued</div><div>{c.issued_at ? new Date(c.issued_at).toLocaleDateString() : "—"}</div></div>
        </div>
      </div>
      <div style={{ fontSize: 11, color: "#5C564D" }}>Modules covered:</div>
      <ol style={{ fontSize: 12, columns: 2, marginTop: 4 }}>
        {(c.lessons || []).map((l, i) => (<li key={i} style={{ marginBottom: 2 }}>{l}</li>))}
      </ol>
    </PrintFrame>
  );
}

function ConsultPrintBase({ url, roleForOther, categoryKey, roleTitle }) {
  const { id } = useParams();
  const { data: c } = useFetch(`${url}/${id}`);
  if (!c) return <PrintFrame title="Consultation"><p>Loading consultation…</p></PrintFrame>;
  const messages = c.messages || [];
  const otherLabel = categoryKey === "body_system" ? "Body system" : "Category";
  return (
    <PrintFrame title={`${roleTitle} consultation · ${c.title}`}>
      <h1 style={{ fontFamily: "serif", fontSize: 26, margin: 0 }}>{roleTitle} Consultation</h1>
      <div style={{ marginTop: 8, fontSize: 12, color: "#5C564D" }}>Ref: {c.id}</div>
      <hr className="print-hr" />
      <table style={{ width: "100%", fontSize: 13 }}>
        <tbody>
          <Row k="Title" v={c.title} />
          <Row k={otherLabel} v={c[categoryKey]} />
          <Row k={categoryKey === "body_system" ? "Patient" : "Client"} v={c.patient_name || c.client_name} />
          <Row k="Status" v={c.status} />
          <Row k="Started" v={new Date(c.created_at).toLocaleString()} />
        </tbody>
      </table>
      <div style={{ marginTop: 16 }}>
        {messages.map((m, i) => (
          <div key={i} style={{ padding: 10, borderLeft: `3px solid ${m.author_role === roleForOther ? "#2C4A3B" : "#D49A36"}`, marginBottom: 8, background: m.author_role === "system" ? "#F2EBE5" : "transparent" }}>
            <div style={{ fontSize: 11, fontWeight: 700 }}>
              {m.author_role === roleForOther ? `${roleTitle === "Legal" ? "Adv." : "Dr."} ${m.author_name}` : m.author_name}
              {m.specialization ? ` · ${m.specialization}` : ""}
              {m.kind && m.kind !== "text" ? ` · ${m.kind.replace(/_/g, " ")}` : ""}
              <span style={{ float: "right", fontWeight: 400, color: "#5C564D" }}>{new Date(m.created_at).toLocaleString()}</span>
            </div>
            {m.text && <div style={{ fontSize: 13, marginTop: 4, whiteSpace: "pre-wrap" }}>{m.text}</div>}
            {m.attachments?.length > 0 && (
              <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 6 }}>
                {m.attachments.map((u, k) => (
                  <img key={k} src={u} alt="attachment" style={{ width: 100, height: 100, objectFit: "cover", border: "1px solid #E5D9CC" }} />
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </PrintFrame>
  );
}

export function PrintDoctorConsult() {
  return <ConsultPrintBase url="/consultations" roleForOther="doctor" categoryKey="body_system" roleTitle="Doctor" />;
}
export function PrintLegalConsult() {
  return <ConsultPrintBase url="/legal-consultations" roleForOther="lawyer" categoryKey="category" roleTitle="Legal" />;
}

export function PrintEventTicket() {
  const { id } = useParams();
  const { data: t } = useFetch(`/events/rsvp/${id}`);
  if (!t) return <PrintFrame title="Event Ticket"><p>Loading ticket…</p></PrintFrame>;
  const verifyUrl = `${window.location.origin}/events/verify/${t.id || id}`;
  return (
    <PrintFrame title={`Ticket · ${t.event_title}`}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, letterSpacing: 3, color: "#D95A41", fontWeight: 700 }}>ANVESHAN EVENT PASS</div>
          <h1 style={{ fontFamily: "serif", fontSize: 30, margin: "8px 0" }}>{t.event_title}</h1>
          <div style={{ fontSize: 13, color: "#5C564D" }}>{new Date(t.event_date).toLocaleString()}</div>
          <div style={{ fontSize: 13, color: "#5C564D" }}>{t.event_venue}</div>
        </div>
        <div style={{ textAlign: "center", border: "2px dashed #2C4A3B", padding: 12, borderRadius: 12 }}>
          <div style={{ fontSize: 10, color: "#5C564D" }}>Ticket</div>
          <div style={{ fontFamily: "monospace", fontSize: 20, fontWeight: 700 }}>{t.ticket_number}</div>
          <div style={{ marginTop: 8, background: "#fff", padding: 6, borderRadius: 8, display: "inline-block" }}>
            <QRCodeSVG value={verifyUrl} size={110} level="M" includeMargin={false} />
          </div>
          <div style={{ fontSize: 9, color: "#5C564D", marginTop: 4, maxWidth: 130 }}>Scan at the gate to verify</div>
        </div>
      </div>
      <hr className="print-hr" />
      <table style={{ width: "100%", fontSize: 13 }}>
        <tbody>
          <Row k="Attendee" v={t.user_name} />
          <Row k="Email" v={t.user_email} />
          <Row k="Issued" v={new Date(t.created_at).toLocaleString()} />
          {t.checked_in && <Row k="Checked in" v={t.checked_in_at ? new Date(t.checked_in_at).toLocaleString() : "yes"} />}
        </tbody>
      </table>
      <p style={{ fontSize: 11, color: "#5C564D", marginTop: 16 }}>Please carry this pass (printed or on phone) to the venue. Volunteers will scan the QR to verify.</p>
    </PrintFrame>
  );
}

function Row({ k, v }) {
  return (
    <tr>
      <td style={{ padding: "4px 8px 4px 0", color: "#5C564D", width: 130 }}>{k}</td>
      <td style={{ padding: "4px 0", fontWeight: 600 }}>{v || "—"}</td>
    </tr>
  );
}
